import { X, Funnel } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const FunnelModal = ({ isOpen, onClose, onSave, funnel = null }) => {
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    steps: [""],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") onClose();
    };

    if (isOpen) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const handleOverlayClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    if (funnel) {
      setFormData({
        name: funnel.name || "",
        steps: funnel.steps && funnel.steps.length > 0 ? funnel.steps : [""],
      });
    } else {
      setFormData({ name: "", steps: [""] });
    }
    setErrors({});
  }, [funnel, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Nome é obrigatório";
    if (!formData.steps || formData.steps.length === 0)
      newErrors.steps = "Adicione pelo menos uma etapa";
    else {
      const stepErrors = formData.steps.map((s) =>
        !s.trim() ? "Etapa obrigatória" : "",
      );
      if (stepErrors.some((e) => e !== "")) newErrors.steps = stepErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleStepChange = (e, index) => {
    const newSteps = [...formData.steps];
    newSteps[index] = e.target.value;
    setFormData((prev) => ({ ...prev, steps: newSteps }));

    if (errors.steps && Array.isArray(errors.steps)) {
      const updatedErrors = [...errors.steps];
      updatedErrors[index] = "";
      setErrors((prev) => ({ ...prev, steps: updatedErrors }));
    }
  };

  const addStep = () => {
    setFormData((prev) => ({ ...prev, steps: [...prev.steps, ""] }));
  };

  const removeStep = (index) => {
    const newSteps = formData.steps.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, steps: newSteps }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave({
        ...formData,
        id: funnel?.id,
        usageCount: funnel?.usageCount || 0,
        createdAt: funnel?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        >
          <motion.div
            ref={modalRef}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-2xl rounded-lg bg-white shadow-xl dark:bg-gray-900"
          >
            {/* Cabeçalho */}
            <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
              <h2 className="flex items-center text-xl font-semibold text-gray-900 dark:text-gray-100">
                <Funnel className="mr-2 h-5 w-5" />
                {funnel ? "Editar Funil" : "Novo Funil"}
              </h2>
              <button
                onClick={onClose}
                className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-5">
                {/* Nome do Funil */}
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Funnel className="mr-2 h-4 w-4" />
                    Nome do Funil *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full rounded-lg border ${
                      errors.name
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                    } bg-white px-4 py-2 text-gray-900 transition-colors focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                    placeholder="Digite o nome do funil"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                {/* Etapas */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Etapas *
                  </label>

                  <div className="space-y-3">
                    {formData.steps.map((step, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          name={`step-${index}`}
                          value={step}
                          onChange={(e) => handleStepChange(e, index)}
                          className={`flex-1 rounded-lg border ${
                            errors.steps && errors.steps[index]
                              ? "border-red-500"
                              : "border-gray-300"
                          } bg-white px-4 py-2 text-gray-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                          placeholder={`Etapa ${index + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeStep(index)}
                          className="rounded-lg bg-red-500 px-3 py-2 text-sm text-white transition-colors hover:bg-red-600"
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addStep}
                    className="mt-3 rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-600"
                  >
                    Adicionar Etapa
                  </button>

                  {errors.steps && typeof errors.steps === "string" && (
                    <p className="mt-1 text-sm text-red-500">{errors.steps}</p>
                  )}
                </div>

                {/* Preview */}
                <div className="border-t border-gray-200 p-4 dark:border-gray-700">
                  <div>
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Pré-visualização:
                    </span>
                    <div className="mt-2 space-y-1 rounded-lg border border-gray-300 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
                      <p className="font-semibold text-white">
                        {formData.name || "Nome do Funil"}
                      </p>
                      <ul className="list-disc pl-5 text-sm text-gray-700 dark:text-gray-300">
                        {formData.steps.length > 0 ? (
                          formData.steps.map((s, i) => (
                            <li key={i}>{s || "Etapa sem nome"}</li>
                          ))
                        ) : (
                          <li>Nenhuma etapa adicionada</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botões de ação */}
              <div className="flex justify-end space-x-3 border-t border-gray-200 pt-6 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-600"
                >
                  {funnel ? "Salvar Alterações" : "Criar Funil"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FunnelModal;
